import { Timestamps } from '@/api/global/types/timestamps'
import { SubtaskStatus } from '@/api/pathways/subtasks/types'
import { IdMap } from '@/legacy/types/api/store'
import { Task, TaskTemplate } from '@/legacy/types/pathways/tasks'
import { UUID } from '@/libs/typing/base'
import { CommonTemplateBase, StatusReasonMetadata } from './common'
import { openSubtaskStatuses } from './subtasks'

// TYPES --------------------
export type PathwayPayload = {
  notes: string
  status: PathwayStatus
  goalIds: string[]
  subStatusReason?: SubStatusReason | null
  statusNotes?: string | null
}

export type MapPathwayTask = {
  mappingId: UUID
  mappingTemplateId: UUID
  pathwayId: UUID
  taskId: UUID
  sortNumber: number
}

export type ApiPathway = {
  description: string
  goalIds: string[]
  memberId: string
  notes: string | null
  pathwayId: string
  pathwayVariantId: string
  status: PathwayStatus
  statusReason: StatusReason | null
  statusReasonJson: StatusReasonMetadata | null
  statusUpdatedAt: string
  title: string
  generatingTriggerId: UUID | null
  tasks?: Task[]
  taskMaps?: MapPathwayTask[]
} & Timestamps

export type Pathway = {
  description: string
  goalIds: string[]
  memberId: string
  notes: string | null
  pathwayId: string
  pathwayVariantId: string
  status: PathwayStatus
  statusReason: StatusReason | null
  statusReasonJson: StatusReasonMetadata | null
  statusUpdatedAt: string
  title: string
  generatingTriggerId: UUID | null
  tasks?: Task[]
  taskIds?: string[]
  taskMaps?: MapPathwayTask[]
  variantDisplay?: string
} & Timestamps

export type PathwayTemplate = {
  description: string
  pathwayKey: string
  pathwayTemplateId: string
  baseTemplateId: string
  taskTemplates?: TaskTemplate[]
  templateStatus: TemplateStatus
  title: string
  variantId: string
  variantKey: string | null
  variantDisplay: string
} & Timestamps

export type PathwayTemplateBase = {
  escalationTrigger?: string
  metric?: string
  processMap?: string
  trainingDeck?: string
  faqs?: string
  pathwayKey: string
  title: string
  variants?: PathwayTemplate[]
} & CommonTemplateBase

// INTERFACES --------------------
export interface PathwayState {
  openPathwayMenu: boolean
}

export interface PathwayDataState {
  isLoading: boolean
  error: Error | null
  data: IdMap<Pathway> | null
  isLoaded: boolean
}

export interface PathwayTemplateDataState {
  isLoading: boolean
  error: Error | null
  data: IdMap<PathwayTemplate> | null
  isLoaded: boolean
  queryMetadata: {
    total: number
  } | null
}

// ENUMS --------------------
export enum AddModalActions {
  ADD = 'add',
  REMOVE = 'remove',
}

export enum StatusReason {
  MANUALLY_SET = 'MANUALLY_SET',
  PROPEGATED_FROM_PARENT = 'PROPEGATED_FROM_PARENT',
  BLOCKED_BY_UPSTREAM = 'BLOCKED_BY_UPSTREAM',
  BLOCKED_BY_MIN_TIME = 'BLOCKED_BY_MIN_TIME',
  AUTOTRIGGER = 'AUTOTRIGGER',
  STALE_CLEANUP = 'STALE_CLEANUP',
  AUTOTEXTING = 'AUTOTEXTING',
  POINTER_COMM_STATUS_UPDATED = 'POINTER_COMM_STATUS_UPDATED',
  CREATED = 'CREATED',
  UNBLOCKED_BY_MIN_TIME = 'UNBLOCKED_BY_MIN_TIME',
  UNBLOCKED_BY_UPSTREAM = 'UNBLOCKED_BY_UPSTREAM',
  MAX_ATTEMPTS_REACHED = 'MAX_ATTEMPTS_REACHED',

  // NOTE: this is a cancellation reason reserved for bulk edits
  // only to be used by tech team for cleanup, for easy exclusion from reporting
  // (e.g. cancelling subtasks that shouldn't have been created by data pipelines)
  TECH_INTERVENTION_DATA_CLEANUP = 'TECH_INTERVENTION_DATA_CLEANUP',
}

export enum TemplateStatus {
  DRAFT = 'DRAFT',
  PUBLISHED = 'PUBLISHED',
  ARCHIVED = 'ARCHIVED',
}

export enum TaskStatus {
  ACTIVE = 'ACTIVE',
  ON_HOLD = 'ON_HOLD',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
  BLOCKED = 'BLOCKED',
}

export enum SubStatusReason {
  // Cancelled -- to be deprecated
  EXPIRED = 'EXPIRED',
  NOT_REQUIRED = 'NOT_REQUIRED',
  MEMBER_REQUEST = 'MEMBER_REQUEST',

  // Cancelled
  DUPLICATE = 'DUPLICATE',
  INACCURATE = 'INACCURATE',
  WONT_DO_EXPIRED_LINK = 'WONT_DO_EXPIRED_LINK',
  WONT_DO_NO_LONGER_RELEVANT = 'WONT_DO_NO_LONGER_RELEVANT',
  WONT_DO_MEMBER_DECLINES_TOO_BUSY = 'WONT_DO_MEMBER_DECLINES_TOO_BUSY',
  WONT_DO_MEMBER_DECLINES_TOO_UNWELL = 'WONT_DO_MEMBER_DECLINES_TOO_UNWELL',
  WONT_DO_MEMBER_DECLINES_UNINTERESTED = 'WONT_DO_MEMBER_DECLINES_UNINTERESTED',
  WONT_DO_MEMBER_NEED_RESOLVED = 'WONT_DO_MEMBER_NEED_RESOLVED',
  WONT_DO_UNABLE_TO_COMPLETE_PRE_STEP = 'WONT_DO_UNABLE_TO_COMPLETE_PRE_STEP',
  WONT_DO_ON_DEMAND = 'WONT_DO_ON_DEMAND',
  WONT_DO_OFFBOARDING = 'WONT_DO_OFFBOARDING',
  WONT_DO_HOSPICE = 'WONT_DO_HOSPICE',
  WONT_DO_DECEASED = 'WONT_DO_DECEASED',
  MAXIMUM_ATTEMPTS_REACHED = 'MAXIMUM_ATTEMPTS_REACHED', // not intended for manual selection
  FAILED_TO_CONNECT = 'FAILED_TO_CONNECT',

  // Cancelled or On Hold
  OTHER = 'OTHER',

  // On Hold
  DO_AT_LATER_DATE = 'DO_AT_LATER_DATE',
}

export enum PathwayStatus {
  ACTIVE = 'ACTIVE',
  ON_HOLD = 'ON_HOLD',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
  BLOCKED = 'BLOCKED',
  SUGGESTED = 'SUGGESTED',
  SUGGESTION_REJECTED = 'SUGGESTION_REJECTED',
}

export enum VariantKey {
  High = 'high_acuity',
  Medium = 'medium_acuity',
  Low = 'low_acuity',
}

// CONSTANTS --------------------
export const DIARRHEA_PATHWAY_NAME = 'Diarrhea'

export const GENERIC_COMPLETED_STATUS = 'COMPLETED'

export const pathwayStatusLabels = [
  { label: 'Current', value: PathwayStatus.ACTIVE },
  { label: 'Blocked', value: PathwayStatus.BLOCKED },
  { label: 'On Hold', value: PathwayStatus.ON_HOLD },
  { label: 'Completed', value: PathwayStatus.COMPLETED },
  { label: 'Canceled', value: PathwayStatus.CANCELLED },
  { label: 'Suggested', value: PathwayStatus.SUGGESTED },
  { label: 'Suggestion Rejected', value: PathwayStatus.SUGGESTION_REJECTED },
]

export const taskStatusLabels = [
  { label: 'Current', value: TaskStatus.ACTIVE },
  { label: 'Blocked', value: TaskStatus.BLOCKED },
  { label: 'On Hold', value: TaskStatus.ON_HOLD },
  { label: 'Completed', value: TaskStatus.COMPLETED },
  { label: 'Canceled', value: TaskStatus.CANCELLED },
]

export const closedTaskStatuses = [TaskStatus.CANCELLED, TaskStatus.COMPLETED]

export const closedPathwayStatuses = [
  PathwayStatus.CANCELLED,
  PathwayStatus.COMPLETED,
  PathwayStatus.SUGGESTION_REJECTED,
]

export const suggestionPathwayStatuses = [
  PathwayStatus.SUGGESTED,
  PathwayStatus.SUGGESTION_REJECTED,
]

export type StatusDetails = {
  text: string
  icon: string
  value: SubtaskStatus
  fillType: string | null
  statusOptions: SubtaskStatus[]
}

export const SubtaskStatuses: { [Key: string]: StatusDetails } = {
  OPEN_UNASSIGNED: {
    text: 'Open Unassigned',
    icon: 'openStatus',
    value: SubtaskStatus.OPEN_UNASSIGNED,
    fillType: 'nash-neutral600',
    statusOptions: [
      ...openSubtaskStatuses,
      SubtaskStatus.CANCELLED,
      SubtaskStatus.ON_HOLD,
    ],
  },
  OPEN_ASSIGNED: {
    text: 'Open Assigned',
    icon: 'openStatus',
    value: SubtaskStatus.OPEN_ASSIGNED,
    fillType: 'nash-neutral600',
    statusOptions: [
      ...openSubtaskStatuses,
      SubtaskStatus.COMPLETED,
      SubtaskStatus.CANCELLED,
      SubtaskStatus.ON_HOLD,
    ],
  },
  IN_PROGRESS: {
    text: 'In Progress',
    icon: 'inProgressStatus',
    value: SubtaskStatus.IN_PROGRESS,
    fillType: 'nash-neutral600',
    statusOptions: [
      SubtaskStatus.REATTEMPT,
      SubtaskStatus.IN_PROGRESS,
      SubtaskStatus.OPEN_ASSIGNED,
      SubtaskStatus.COMPLETED,
      SubtaskStatus.CANCELLED,
      SubtaskStatus.ON_HOLD,
    ],
  },
  REATTEMPT: {
    text: 'Reattempt',
    icon: 'reattempt',
    value: SubtaskStatus.REATTEMPT,
    fillType: 'nash-teal700',
    statusOptions: [
      SubtaskStatus.REATTEMPT,
      SubtaskStatus.IN_PROGRESS,
      SubtaskStatus.OPEN_ASSIGNED,
      SubtaskStatus.COMPLETED,
      SubtaskStatus.CANCELLED,
      SubtaskStatus.ON_HOLD,
    ],
  },
  COMPLETED: {
    text: 'Completed',
    icon: 'completedStatus',
    value: SubtaskStatus.COMPLETED,
    fillType: 'nash-fern600',
    statusOptions: [SubtaskStatus.CLOSED_IN_ERROR, SubtaskStatus.COMPLETED],
  },
  ON_HOLD: {
    text: 'On Hold',
    icon: 'onHoldStatus',
    value: SubtaskStatus.ON_HOLD,
    fillType: 'nash-purple500',
    statusOptions: [
      SubtaskStatus.IN_PROGRESS,
      SubtaskStatus.OPEN_ASSIGNED,
      SubtaskStatus.CANCELLED,
      SubtaskStatus.ON_HOLD,
    ],
  },
  BLOCKED: {
    text: 'Blocked',
    icon: 'blockedStatus',
    value: SubtaskStatus.BLOCKED,
    fillType: 'nash-brick700',
    statusOptions: [SubtaskStatus.BLOCKED, SubtaskStatus.CANCELLED],
  },
  CANCELLED: {
    text: 'Canceled',
    icon: 'cancelledStatus',
    value: SubtaskStatus.CANCELLED,
    fillType: 'nash-neutral600',
    statusOptions: [SubtaskStatus.CLOSED_IN_ERROR, SubtaskStatus.CANCELLED],
  },
  CLOSED_IN_ERROR: {
    text: 'Closed In Error',
    icon: 'cancelledStatus',
    value: SubtaskStatus.CLOSED_IN_ERROR,
    fillType: 'nash-neutral600',
    statusOptions: [SubtaskStatus.CLOSED_IN_ERROR],
  },
}
